import { PaymentStatus } from './../../models/payment-status.enum';
import { ReportType } from './../../models/report-type.enum';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { MenuStore } from '../../../menus/service/menu.store';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { DateUtil } from '../../../shared/utils/date.util';
import { AdjustmentSign } from '../../models/adjustment-sign.enum';
import { ConfigLookupType } from '../../models/config/config-lookup-type';
import { ConfigResponse } from '../../models/config/config-response';
import { OnyxEvent } from '../../models/event';
import { Payment } from '../../models/payment';
import { EventStore } from '../../service/event.store';
import { GroupPayConfigStore } from '../../service/grouppay-config.store';
import { PaymentStore } from '../../service/payment.store';
import { EventUtils } from '../../utils/event-utils';
import { ReportService } from '../../service/report.service';
import { ResponseUtil } from '../../../shared/utils/response-util';

@Component({
  selector: 'pioneer-commission',
  templateUrl: './commission.component.html',
  styleUrls: ['./commission.component.css']
})
export class CommissionComponent implements OnInit {

  commissionForm: FormGroup;
  onyxEvent: OnyxEvent;
  latestPayment: Payment;
  finalPaymentStatusList: Payment[];
  prepayment: Payment;
  displayedPrepayment = false;
  isCollapsedAddAdjustment = true;
  private hasPrepayment: boolean;
  private isFinalPaymentAuthorized: boolean;
  canMakePrepayment = false;
  showPrepaymentHintText = true;

  ui = {
    showAddPayment: false,
    showPaymentRecord: false,
    showPaymentSummary: false,
    showDownloadDropdown: false
  };

  minAmountPrepayment: number;

  constructor(
    private formBuilder: FormBuilder,
    private menuStore: MenuStore,
    private eventStore: EventStore,
    private paymentStore: PaymentStore,
    private authenticationStore: AuthenticationStore,
    private groupPayConfigStore: GroupPayConfigStore,
    private reportService: ReportService
  ) { }

  ngOnInit() {

    this.eventStore.selectedEvent.subscribe((event: OnyxEvent) => {
      this.onyxEvent = new OnyxEvent(event);
    });

    this.paymentStore.getFinalPaymentStatusList().subscribe((finalPaymentStatusList: Payment[]) => {
      this.finalPaymentStatusList = finalPaymentStatusList;
    });

    this.paymentStore.getLatestFinalPayment().subscribe((latestPayment: Payment) => {
      this.latestPayment = latestPayment;
      const latestFinalPaymentStatus = this.latestPayment ? this.latestPayment.status : undefined;
      if (latestFinalPaymentStatus) {
        this.ui.showPaymentSummary = latestFinalPaymentStatus !== PaymentStatus.REQUEST_REJECTED;
        this.ui.showAddPayment = latestFinalPaymentStatus === PaymentStatus.REQUEST_REJECTED;
        this.ui.showDownloadDropdown = true;
        this.isFinalPaymentAuthorized = true;
      } else {
        this.ui.showPaymentSummary = false;
        this.ui.showAddPayment = true;
        this.ui.showDownloadDropdown = false;
        this.isFinalPaymentAuthorized = false;
      }
      this.getPrepayment().subscribe(prepayment => {
        this.prepayment = prepayment;
        this.hasPrepayment = prepayment ? true : false;
        if (latestFinalPaymentStatus || this.hasPrepayment) {
          this.ui.showPaymentRecord = true;
        } else {
          this.ui.showPaymentRecord = false;
        }
      });
    });

    this.commissionForm = this.formBuilder.group({});
    if (this.onyxEvent && this.onyxEvent.name) {
      this.menuStore.setPageHeader(this.onyxEvent.name, this.onyxEvent.eventProperty.subscriber);
    } else {
      this.menuStore.setPageHeader('EVENT.EVENT_INFO_TITLE', false);
    }

    this.getMinAmount().subscribe(minAmount => {
      this.minAmountPrepayment = minAmount;
    });

    this.checkPrepaymentPermissions();
  }

  displayPrepaymentClick(): void {
    this.displayedPrepayment = true;
  }

  onAddPrepayment(): void {
    this.displayedPrepayment = false;
    this.hasPrepayment = true;
  }

  onCancelPrepayment(): void {
    this.displayedPrepayment = false;
  }

  isUserApprover(): boolean {
    return this.authenticationStore.hasPermission('AuthorizePayment');
  }

  displayAddPrepaymentButton(): boolean {
    return !this.displayedPrepayment && !this.hasPrepayment && !this.isEventStarted() && !this.isFinalPaymentAuthorized
      && !this.authenticationStore.isAgencyUser();
  }

  private isEventStarted(): boolean {
    const eventStartDate = DateUtil.parseDate(this.onyxEvent.startDate).getTime();
    return (eventStartDate < Date.now());
  }

  private getPrepayment(): Observable<Payment> {
    return this.paymentStore.getPrepayment().map((prepayment) => {
      return prepayment && prepayment.status ? prepayment : null;
    }).share();
  }

  private getMinAmount(): Observable<number> {
    return this.groupPayConfigStore.loadConfig(ConfigLookupType.PREPAYMENT_MIN_AMOUNT, this.onyxEvent.chain, this.onyxEvent.brand,
      this.onyxEvent.propertyId, this.onyxEvent.agencyId)
        .map((response: ConfigResponse) => {
          return response ? JSON.parse(response.value) : null;
        });
  }

  private checkPrepaymentPermissions(): void {
    this.showPrepaymentHintText = this.displayAddPrepaymentButton() && !EventUtils.doesEventHaveAgency(this.onyxEvent);
    this.canMakePrepayment = this.isUserApprover() && EventUtils.doesEventHaveAgency(this.onyxEvent);
  }

  addAdjustmentClick(): void {
    this.isCollapsedAddAdjustment = false;
  }

  displayAddAdjustmentButton(): boolean {
    return this.ui.showAddPayment && this.isCollapsedAddAdjustment;
  }

  /**
   *  If it is an agency user and the event property is subscriber,
   *  the addAdjustment button must be disabled
   * @returns {boolean}
   */
  disableAddAdjustmentButton(): boolean {
    let disable: boolean;
    if (this.authenticationStore.isAgencyUser() && this.onyxEvent.eventProperty.subscriber) {
      disable = true;
    } else {
      disable = false;
    }
    return disable;
  }

  onAddAdjustment(): void {
    this.isCollapsedAddAdjustment = true;
  }

  onCancelAdjustment(): void {
    this.isCollapsedAddAdjustment = true;
  }

  getPaymentsReport(fileType: string) {
    this.reportService.getPaymentsReport(this.onyxEvent.eventId, fileType)
      .subscribe(blob => {
        let filename = ResponseUtil.getFileNameFromHttpResponse(blob);
        if (!filename) {
          filename = this.reportService.buildReportFileName(ReportType.PAYMENT_SUMMARY, this.onyxEvent, fileType);
        }
        saveAs(blob.body, filename);
    });
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/payment/commission/commission.component.ts